/* Yoreflix Dashboard — LAYOUT, and the only file allowed to own it.
 *
 * ★★★ WHY THIS FILE EXISTS AT ALL. This portal was written with inline `CSSProperties` objects,
 * copied from the admin portal where a desktop-only layout was fine. Inline styles CANNOT carry a
 * media query — and worse, they BEAT any class that tries to override them (the same specificity
 * trap already on record from admin, where inline style beat `:hover`). So responsive work here is
 * not "add breakpoints": every layout-bearing property has to LEAVE the inline objects and live
 * here. Colour, type and spacing tokens stay inline; anything that arranges boxes is in this file.
 * If you find yourself adding `display`, `grid-template-columns` or `flex-direction` to a style
 * object in a .tsx, it will not respond — put it here instead.
 *
 * David, 2026-07-23: "it would be very nice to be able to view this effectively on a phone/tablet
 * (this wasnt so important for the admin portal)".
 *
 * THE BREAKPOINTS — three, named for what they change, not for devices:
 *   (max-width: 639px)  PHONE   — one column; menu and tree become overlays.
 *   640px .. 1023px     TABLET  — two columns where they fit; menu still an overlay.
 *   >= 1024px           DESK    — the full three-pane layout; menu is a persistent pane.
 */

/* ============================ busy indicator ============================ */

/* ★★ ANY BUTTON THAT WAITS ON THE NETWORK SHOWS THIS, IMMEDIATELY ON CLICK. Sign-out takes about
 * three seconds against the API and said nothing while it worked, so the button read as dead and
 * invited a second click (David, 2026-07-23). The feedback has to land on the CLICK, not on the
 * response — the same rule as landing a drop before its request. */
.dash-spinner {
  display: inline-block;
  flex: 0 0 auto;
  width: 1em;
  height: 1em;
  vertical-align: -0.15em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: dash-spin 700ms linear infinite;
}

@keyframes dash-spin {
  to { transform: rotate(360deg); }
}

/* ⚠ Slowed, NOT stopped. A spinner that does not move is not a spinner, and someone who asked for
 * less motion still needs to know the thing is working. */
@media (prefers-reduced-motion: reduce) {
  .dash-spinner { animation-duration: 2.4s; }
}

/* ⚠ The page title is dropped from the top bar on a phone — see Topbar.tsx. The brand and the
 * account chip win the strip; the screen name is already on the active menu item. */
@media (max-width: 639px) {
  .dash-topbar-title { display: none; }
}

/* ============================ shell ============================ */

.dash-shell { display: flex; flex-direction: column; height: 100%; }
.dash-body { display: flex; flex: 1 1 auto; min-height: 0; position: relative; }
.dash-main { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }

/* ★★ THE MENU. A persistent pane at desktop; an overlay that slides in at phone and tablet.
 * David, 2026-07-23: "menu becomes slide out - select option - auto side back on mobile" — the
 * auto-close is MOBILE ONLY and lives in the component; closing a persistent desktop pane every
 * time you picked something would be maddening. */
/* ⚠ `display:flex` so the SideMenu inside STRETCHES to the wrapper's full height. The wrapper is
 * full-height in both modes (a flex item of the body at desktop, `inset:0` when it slides), but the
 * <aside> it wraps sizes to its content, so without this the slide-in panel stopped at the build
 * stamp and the scrim showed through below it (David, phone). A row flex with default
 * align-items:stretch fills the cross axis — the panel's height — while its own width is untouched. */
.dash-menu { flex: 0 0 auto; display: flex; }
.dash-menu > * { min-height: 100%; }

@media (max-width: 1023px) {
  .dash-menu {
    position: absolute;
    inset: 0 auto 0 0;
    z-index: 40;
    transform: translateX(-100%);
    /* ⚠ `visibility` as well as `transform`: a panel slid off-screen is still in the tab order and
       still read by a screen reader, so a closed menu would silently swallow keyboard focus. */
    visibility: hidden;
    transition: transform 180ms ease, visibility 180ms ease;
    box-shadow: 0 0 24px rgb(0 0 0 / 18%);
  }
  .dash-menu[data-open='true'] { transform: translateX(0); visibility: visible; }
  /* ★★ ASYMMETRIC FADE (David): the darkening creeps in over 2s as the menu slides out, then lifts
     in 600ms on the way back. The trick is that a transition belongs to the state you are moving
     TO — the long duration lives on [data-open='true'] (the fade IN), the short one on the base
     rule (the fade OUT) — so each direction gets its own timing from one element. It must stay
     mounted for both to play, which is why the shell renders it for all of overlay mode. */
  .dash-scrim {
    position: absolute;
    inset: 0;
    z-index: 30;
    background: rgb(0 0 0 / 35%);
    border: 0;
    padding: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 600ms ease; /* fade OUT — slide back */
  }
  .dash-scrim[data-open='true'] {
    opacity: 1;
    pointer-events: auto;
    transition: opacity 2000ms ease; /* fade IN — slide out */
  }
}

/* Nothing to slide, and no scrim, once the menu is a real pane. */
@media (min-width: 1024px) {
  .dash-scrim { display: none; }
}

/* ⚠ Respect a user who has asked the OS to stop moving things. The panel still opens; it just
 * appears instead of sliding. */
@media (prefers-reduced-motion: reduce) {
  .dash-menu { transition: none; }
  /* The deliberate 2s/600ms scrim fades are motion too — collapse them for anyone who opted out. */
  .dash-scrim,
  .dash-scrim[data-open='true'] { transition-duration: 1ms; }
}

/* ============================ the axes bar ============================ */

/* v1's page-level control bar: one row on a wide screen, wrapping to as many rows as it needs on
 * a narrow one. Native <select> is kept deliberately — on a phone it opens the OS picker, which
 * beats any custom dropdown we could write. */
.dash-axes {
  display: flex;
  align-items: center;
  gap: 10px 14px;
  flex-wrap: wrap;
  padding: 10px 24px;
  border-bottom: 1px solid var(--border);
  flex: 0 0 auto;
}
.dash-axis { display: flex; align-items: center; gap: 6px; min-width: 0; }
.dash-axes select { min-height: 44px; } /* touch target */

@media (max-width: 639px) {
  .dash-axes { padding: 10px 14px; }
  /* Each axis takes half the row, so four axes land as a tidy 2x2 rather than a ragged stack. */
  .dash-axis { flex: 1 1 calc(50% - 14px); }
  .dash-axis select { flex: 1 1 auto; min-width: 0; }
}

/* ============================ page furniture ============================ */

.dash-page { padding: 24px; max-width: 1000px; }
/* The master/detail screen earns more width than a single column of prose does — a tree pane plus
   a five-column table inside 1000px leaves the title column too narrow to read. */
.dash-page--wide { max-width: 1320px; }
@media (max-width: 639px) { .dash-page { padding: 14px; } }

/* ★★ BOTH METRICS ARE ALWAYS ON SCREEN — v1 shows Views and Watch Time side by side and never
 * offers a toggle, which is what makes it read as slicing rather than navigating. At phone width
 * side-by-side is not possible, so they STACK: both still present in one scroll, still no toggle,
 * still impossible to mistake one for the other. The requirement survives; the geometry does not. */
.dash-metrics { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; align-items: start; }
@media (max-width: 767px) { .dash-metrics { grid-template-columns: 1fr; gap: 18px; } }

.dash-tiles { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
@media (max-width: 767px) { .dash-tiles { grid-template-columns: 1fr; } }

/* Tree beside detail at desktop; at phone and tablet the tree is a sheet and only the detail
 * occupies the page. */
.dash-split { display: grid; grid-template-columns: minmax(220px, 300px) minmax(0, 1fr); gap: 20px; align-items: start; }
@media (max-width: 1023px) { .dash-split { grid-template-columns: minmax(0, 1fr); } }

/* ============================ ranking bars ============================ */

/* ★★ THE LAYOUT THAT ALSO FIXES v1'S WORST FLAW. v1 puts the label to the RIGHT of the bar, so at
 * `Per episode` — where the label is the full breadcrumb — the text overruns the bar and the
 * percentage underneath becomes unreadable. Label on its own line with the value right-aligned,
 * bar full width beneath: works at every width, and the tree supplies the context that the
 * breadcrumb was carrying. */
.dash-rank { display: flex; flex-direction: column; gap: 12px; }
.dash-rank-row { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.dash-rank-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; min-width: 0; }
.dash-rank-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.dash-rank-value { flex: 0 0 auto; font-family: var(--font-number); }
.dash-rank-track { height: 8px; border-radius: 999px; background: var(--muted); overflow: hidden; }
.dash-rank-fill { height: 100%; border-radius: 999px; background: var(--primary-dark); }

/* ============================ the titles grid ============================ */

.dash-grid-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 76px 96px 84px 118px;
  gap: 10px;
  align-items: center;
}

/* ⚠ TITLES WRAP, THEY DO NOT ELLIPSIS. At per-episode depth the name is the ONLY thing telling one
   row from the next, and a single-line ellipsis rendered every row of a German drama as "Episod…"
   — the same failure as v1's breadcrumb overrunning its own bar, arrived at from the other side.

   ⚠ PLAIN WRAPPING, NOT `-webkit-line-clamp`. The clamp was tried first and silently did nothing:
   the build strips the prefixed `display: -webkit-box` it depends on, so the computed display came
   back as `flow-root` and every row still read "Episod…". A rule whose effect hangs on a vendor
   prefix surviving minification is a rule that works in dev and not in production. */
.dash-title-text {
  white-space: normal;
  overflow-wrap: anywhere;
  min-width: 0;
}

/* ⚠ BELOW THIS WIDTH THE TABLE STOPS BEING A TABLE. Five columns at 380px gives each number about
 * 40px, which truncates the very figures the row exists to show. Each row becomes a card: title on
 * its own line, the four measures wrapped beneath it as labelled pairs. */
@media (max-width: 767px) {
  .dash-grid-row { grid-template-columns: 1fr; gap: 6px; padding-bottom: 10px; }
  .dash-grid-row > .dash-cell { display: flex; justify-content: space-between; gap: 12px; }
  .dash-cell::before {
    content: attr(data-label);
    color: var(--muted-foreground);
    font-size: var(--fs-xs);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  /* ⚠ THE TITLE IS THE CARD'S HEADING, NOT A LABELLED MEASURE. Its data-label is empty, but the
     empty ::before still generated a flex item, and `space-between` then shoved the title into the
     middle of the card — every title floated centred. Drop the phantom label and let the title sit
     left with its type pill trailing it. */
  .dash-cell[data-label='']::before { display: none; }
  .dash-cell[data-label=''] {
    justify-content: flex-start;
    margin-bottom: 4px;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--border);
    font-size: var(--fs-base);
  }
  .dash-grid-head { display: none; }
}

/* ============================ the day / hour charts ============================ */

/* ⚠ THE CHART SCROLLS, THE PAGE NEVER DOES. A quarter is ~90 columns; at phone width that is 4px
 * each. Aggregating to weeks would be the obvious fix and it is the wrong one — the empty days are
 * the shape (see TitleDetail's header), and a week bucket hides exactly the gaps the chart exists
 * to show. So keep one column per day and let the strip scroll inside itself. */
.dash-daystrip { overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; }
.dash-daystrip-inner { display: flex; align-items: flex-end; min-width: 100%; }

/* Wide: the columns share the width, so a whole period fits the card with no scrolling at all. */
.dash-day { flex: 1 1 0; min-width: 1px; }

/* ⚠ Narrow: a 4px column is visible but untappable, and tapping a day is what drives the hour
   chart beneath it. Give the columns a real target and let the strip scroll. */
@media (max-width: 767px) {
  .dash-day { flex: 0 0 auto; min-width: 9px; }
}

/* ============================ the tree ============================ */

.dash-tree { display: flex; flex-direction: column; }
.dash-tree-row {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 40px;
  padding: 4px 8px;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  border-radius: var(--radius);
}
.dash-tree-row[data-selected='true'] { background: var(--primary-verylight); font-weight: 600; }
.dash-tree-twist {
  flex: 0 0 auto;
  width: 28px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: none;
  color: var(--muted-foreground);
  cursor: pointer;
  border-radius: var(--radius);
}
.dash-tree-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex: 1 1 auto; }

/* The tree as a bottom sheet, on the widths where it cannot be a pane. */
@media (max-width: 1023px) {
  .dash-sheet {
    position: fixed;
    inset: auto 0 0 0;
    z-index: 60;
    max-height: 75vh;
    overflow: auto;
    background: var(--card);
    border-top: 1px solid var(--border);
    border-radius: var(--radius) var(--radius) 0 0;
    box-shadow: 0 -8px 28px rgb(0 0 0 / 22%);
    padding: 8px 12px calc(12px + env(safe-area-inset-bottom));
  }
  .dash-sheet-scrim { position: fixed; inset: 0; z-index: 50; background: rgb(0 0 0 / 35%); border: 0; padding: 0; }
}
@media (min-width: 1024px) {
  .dash-sheet-scrim { display: none; }
}

/* Touch targets: 44px is the floor for anything you tap on a phone. */
@media (max-width: 1023px) {
  .dash-tree-row, .dash-tree-twist { min-height: 44px; }
}
